import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  LocalPharmacy as RxIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  Article as ArticleIcon,
  ReportProblem as RiskIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import type { EvolutionAttachedPrintable, EvolutionPrintableCategory } from './evolutionWorkflowTypes';
import PrescriptionFormDialog from '../clinic/PrescriptionFormDialog';
import CertificateFormDialog from '../clinic/CertificateFormDialog';
import OphthalmologyExamOrderSection from './OphthalmologyExamOrderSection';
import {
  buildOphthalmologyExamOrderBody,
  emptyOphthalmologySelections,
  type OphthalmologyExamSelection,
} from './ophthalmologyExamOrderConstants';

const CATEGORY_DEF: {
  key: EvolutionPrintableCategory;
  label: string;
  icon: React.ElementType;
}[] = [
  { key: 'prescription', label: 'Receituários', icon: RxIcon },
  { key: 'certificate', label: 'Atestados', icon: DescriptionIcon },
  { key: 'exam_order', label: 'Solicitações de Exames', icon: ScienceIcon },
  { key: 'report', label: 'Laudos', icon: ArticleIcon },
  { key: 'surgical_risk', label: 'Risco cirúrgico', icon: RiskIcon },
];

interface EvolutionPrintablesWizardProps {
  open: boolean;
  onClose: () => void;
  onCommit: (item: EvolutionAttachedPrintable) => void;
  /** Paciente / profissional da evolução (obrigatório para receituário e atestado completos). */
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
}

/** Assistente: categorias + modelos; receituário/atestado abrem o mesmo fluxo das páginas da clínica. */
const EvolutionPrintablesWizard: React.FC<EvolutionPrintablesWizardProps> = ({
  open,
  onClose,
  onCommit,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
}) => {
  const [category, setCategory] = useState<EvolutionPrintableCategory>('prescription');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState<{ id?: string; name: string } | null>(null);
  const [editorBody, setEditorBody] = useState('');
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);

  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [prefillRxTemplate, setPrefillRxTemplate] = useState<any | null>(null);
  const [prefillCertTemplate, setPrefillCertTemplate] = useState<any | null>(null);

  const [clinicMedicalSpecialty, setClinicMedicalSpecialty] = useState<string | null>(null);
  const [clinicSettingsLoaded, setClinicSettingsLoaded] = useState(false);
  const [ophSelections, setOphSelections] = useState<OphthalmologyExamSelection>(() =>
    emptyOphthalmologySelections(),
  );
  const [ophOutros, setOphOutros] = useState('');
  const [ophObs, setOphObs] = useState('');
  /** Itens prontos no assistente; só vão para a evolução ao confirmar no rodapé. */
  const [assistantQueue, setAssistantQueue] = useState<EvolutionAttachedPrintable[]>([]);

  const categoryLabel = (k: EvolutionPrintableCategory) =>
    CATEGORY_DEF.find((c) => c.key === k)?.label || k;

  const enqueueToAssistant = (printable: EvolutionAttachedPrintable) => {
    setAssistantQueue((prev) => [...prev, printable]);
  };

  const removeFromAssistantQueue = (clientKey: string) => {
    setAssistantQueue((prev) => prev.filter((e) => e.clientKey !== clientKey));
  };

  const flushAssistantQueueAndClose = () => {
    setAssistantQueue((q) => {
      q.forEach((printable) => {
        onCommit(printable);
      });
      return [];
    });
    onClose();
  };

  const discardWizardAndClose = () => {
    setAssistantQueue([]);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setClinicSettingsLoaded(false);
      return;
    }
    let cancelled = false;
    setClinicSettingsLoaded(false);
    axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        if (cancelled) return;
        const v = res.data?.data?.clinic_medical_specialty;
        setClinicMedicalSpecialty(typeof v === 'string' && v.trim() ? v.trim() : null);
      })
      .catch(() => {
        if (!cancelled) setClinicMedicalSpecialty(null);
      })
      .finally(() => {
        if (!cancelled) setClinicSettingsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const loadTemplates = useCallback(async () => {
    if (!open) return;
    if (!clinicSettingsLoaded && category === 'exam_order') return;
    setLoading(true);
    try {
      if (category === 'prescription') {
        const { data } = await axios.get(getApiUrl('/clinic/prescriptions/templates'), {
          params: { search: search || undefined, is_active: true },
        });
        const list = Array.isArray(data?.data) ? data.data : data;
        setRows(Array.isArray(list) ? list : []);
      } else if (category === 'certificate') {
        const { data } = await axios.get(getApiUrl('/clinic/certificates/templates'));
        const list = Array.isArray(data?.data) ? data.data : data;
        setRows(Array.isArray(list) ? list : []);
      } else if (category === 'exam_order') {
        if (clinicMedicalSpecialty === 'oftalmologia') {
          setRows([]);
          setLoading(false);
          return;
        }
        const { data } = await axios.get(getApiUrl('/clinic/exam-orders/templates'), {
          params: { search: search || undefined },
        });
        const list = Array.isArray(data?.data) ? data.data : data;
        setRows(Array.isArray(list) ? list : []);
      } else if (category === 'report' || category === 'surgical_risk') {
        setRows([
          {
            id: `free-${category}`,
            name: `${category === 'report' ? 'Laudo' : 'Risco cirúrgico'} — texto livre`,
            isFree: true,
          },
        ]);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [open, clinicSettingsLoaded, category, search, clinicMedicalSpecialty]);

  useEffect(() => {
    const delay = category === 'report' || category === 'surgical_risk' ? 0 : 280;
    const t = setTimeout(() => {
      if (!open) return;
      if (category === 'exam_order' ? clinicSettingsLoaded : true) loadTemplates();
    }, delay);
    return () => clearTimeout(t);
  }, [open, clinicSettingsLoaded, category, search, loadTemplates]);

  useEffect(() => {
    if (open) {
      setCategory('prescription');
      setSearch('');
      setEditorOpen(false);
      setEditorTemplate(null);
      setEditorBody('');
      setPrescriptionDialogOpen(false);
      setCertificateDialogOpen(false);
      setPrefillRxTemplate(null);
      setPrefillCertTemplate(null);
      setOphSelections(emptyOphthalmologySelections());
      setOphOutros('');
      setOphObs('');
      setAssistantQueue([]);
    }
  }, [open]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        String(r.code || '')
          .toLowerCase()
          .includes(q),
    );
  }, [rows, search]);

  const hasClinicalContext = Boolean(patientId && professionalId);

  const openEditor = (row: any) => {
    const name = row.name || row.title || 'Modelo';
    setEditorTemplate({ id: row.isFree ? undefined : row.id, name });
    setEditorBody('');
    setEditorOpen(true);
  };

  const commitEditor = () => {
    if (!editorTemplate) return;
    const item: EvolutionAttachedPrintable = {
      clientKey: crypto.randomUUID(),
      category,
      templateId: editorTemplate.id,
      templateName: editorTemplate.name,
      bodyText: editorBody.trim(),
    };
    enqueueToAssistant(item);
    setEditorOpen(false);
  };

  const handleClinicalSuccess = (item: EvolutionAttachedPrintable) => {
    enqueueToAssistant(item);
    setPrescriptionDialogOpen(false);
    setCertificateDialogOpen(false);
    setPrefillRxTemplate(null);
    setPrefillCertTemplate(null);
  };

  const openPrescriptionFlow = (tpl: any | null) => {
    setPrefillRxTemplate(tpl);
    setPrescriptionDialogOpen(true);
  };

  const openCertificateFlow = (tpl: any | null) => {
    setPrefillCertTemplate(tpl);
    setCertificateDialogOpen(true);
  };

  const catInfo = CATEGORY_DEF.find((c) => c.key === category);

  const isOphthalmologyExamFlow =
    category === 'exam_order' && clinicSettingsLoaded && clinicMedicalSpecialty === 'oftalmologia';

  const showSimpleTemplateList =
    (category === 'exam_order' && !isOphthalmologyExamFlow) ||
    category === 'report' ||
    category === 'surgical_risk';

  const commitOphthalmologyExamOrder = () => {
    const bodyText = buildOphthalmologyExamOrderBody(ophSelections, ophOutros, ophObs);
    const item: EvolutionAttachedPrintable = {
      clientKey: crypto.randomUUID(),
      category: 'exam_order',
      templateName: 'Solicitação de exames — Oftalmologia',
      bodyText,
    };
    enqueueToAssistant(item);
    setOphSelections(emptyOphthalmologySelections());
    setOphOutros('');
    setOphObs('');
  };

  const openExamFreeTextEditor = () => {
    openEditor({
      id: `free-${category}`,
      name: 'Solicitação de exames — texto livre',
      isFree: true,
    });
  };

  return (
    <>
      <Dialog
        open={open && !editorOpen && !prescriptionDialogOpen && !certificateDialogOpen}
        onClose={discardWizardAndClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#f8fafc',
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: 0.2 }}>
              Impressos e solicitações
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', maxWidth: 520 }}>
              Por tipo à esquerda, use &quot;Adicionar à fila&quot;. Você pode acumular receituário, exames, laudos etc.;
              as <strong>indicações cirúrgicas/de procedimento</strong> ficam na seção própria da evolução (fora deste assistente).
              Ao final confira a fila abaixo e confirme uma vez em &quot;Adicionar à evolução&quot;.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Configurações dos modelos (cadastro da clínica)">
              <IconButton size="small" onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={{ color: '#64748b' }}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={discardWizardAndClose} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}>
          <MenuItem disabled>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            Timbrado / papel: configure em Configurações da clínica
          </MenuItem>
          <MenuItem disabled>
            <ListItemIcon><AssignmentIcon fontSize="small" /></ListItemIcon>
            Modelos de receituário e atestado: menus Receituários e Atestados
          </MenuItem>
        </Menu>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 380, maxHeight: { xs: '75vh', sm: '70vh' }, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          <List
            component="nav"
            sx={{
              width: 228,
              flexShrink: 0,
              py: 1,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fafafa',
            }}
          >
            {CATEGORY_DEF.map((c) => {
              const Ico = c.icon;
              const sel = category === c.key;
              return (
                <ListItemButton
                  key={c.key}
                  selected={sel}
                  onClick={() => setCategory(c.key)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(20, 184, 166, 0.12)',
                      borderLeft: '3px solid #14B8A6',
                    },
                  }}
                >
                  <Ico sx={{ fontSize: 20, mr: 1.5, color: sel ? '#0f766e' : '#64748b' }} />
                  <ListItemText
                    primary={c.label}
                    primaryTypographyProps={{
                      fontWeight: sel ? 700 : 500,
                      fontSize: '0.8125rem',
                      color: sel ? '#0f172a' : '#475569',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: '#14B8A6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                {catInfo?.label}
              </Typography>
              {isOphthalmologyExamFlow && (
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                  Clínica configurada como <strong>Oftalmologia</strong>: marque os exames na grade (OD / OE / AO) ou use texto livre.
                </Alert>
              )}
              {(category === 'prescription' || category === 'certificate') && !hasClinicalContext && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                  Selecione <strong>paciente</strong> e <strong>profissional</strong> na identificação da evolução para emitir
                  {category === 'prescription' ? ' receituário' : ' atestado'} com o cadastro completo.
                </Alert>
              )}
              {showSimpleTemplateList ? (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar modelo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mt: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : null}
              {category === 'prescription' && hasClinicalContext && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar modelo de receituário..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mt: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {category === 'certificate' && hasClinicalContext && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar modelo de atestado..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mt: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
              {category === 'prescription' && hasClinicalContext && (
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openPrescriptionFlow(null)}
                    sx={{
                      py: 1.25,
                      mb: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#14B8A6',
                      '&:hover': { bgcolor: '#0d9488' },
                    }}
                  >
                    Novo receituário (cadastro completo)
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Modelos pré-preenchidos — clique para abrir o editor com o modelo aplicado
                  </Typography>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} sx={{ color: '#14B8A6' }} />
                    </Box>
                  ) : filteredRows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>
                      Nenhum modelo encontrado. Cadastre em Receituários → modelos.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                      {filteredRows.map((tpl: any) => (
                        <Chip
                          key={tpl.id}
                          label={tpl.name || tpl.title}
                          onClick={() => openPrescriptionFlow(tpl)}
                          sx={{ fontWeight: 600, bgcolor: '#e0f2fe', color: '#0369a1' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {category === 'certificate' && hasClinicalContext && (
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openCertificateFlow(null)}
                    sx={{
                      py: 1.25,
                      mb: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#14B8A6',
                      '&:hover': { bgcolor: '#0d9488' },
                    }}
                  >
                    Novo atestado (cadastro completo)
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Modelos — clique para preencher e emitir
                  </Typography>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} sx={{ color: '#14B8A6' }} />
                    </Box>
                  ) : filteredRows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>
                      Nenhum modelo. Cadastre em Atestados.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                      {filteredRows.map((tpl: any) => (
                        <Chip
                          key={tpl.id}
                          label={tpl.title || tpl.name}
                          onClick={() => openCertificateFlow(tpl)}
                          sx={{ fontWeight: 600, bgcolor: '#14B8A615', color: '#14B8A6' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {category === 'exam_order' && !clinicSettingsLoaded && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} sx={{ color: '#14B8A6' }} />
                </Box>
              )}

              {isOphthalmologyExamFlow && (
                <OphthalmologyExamOrderSection
                  selections={ophSelections}
                  onChangeSelections={setOphSelections}
                  outrosText={ophOutros}
                  onChangeOutros={setOphOutros}
                  observacoes={ophObs}
                  onChangeObservacoes={setOphObs}
                  onCommit={commitOphthalmologyExamOrder}
                  onOpenFreeText={openExamFreeTextEditor}
                />
              )}

              {showSimpleTemplateList && (
                <>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={32} sx={{ color: '#14B8A6' }} />
                    </Box>
                  ) : filteredRows.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#94a3b8', p: 2 }}>
                      Nenhum modelo encontrado.
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {filteredRows.map((row, idx) => {
                        const name = row.name || row.title || `Modelo ${idx + 1}`;
                        return (
                          <ListItemButton
                            key={row.id || idx}
                            onClick={() => openEditor({ ...row, name })}
                            sx={{
                              borderRadius: 2,
                              mb: 0.5,
                              py: 1.25,
                              '&:hover': { bgcolor: 'rgba(20, 184, 166, 0.06)' },
                            }}
                          >
                            <ListItemText
                              primary={name}
                              secondary={row.description ? String(row.description).slice(0, 80) : undefined}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                            />
                            <Typography variant="caption" sx={{ color: '#14B8A6', fontWeight: 600 }}>
                              Abrir
                            </Typography>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </>
              )}

              {(category === 'prescription' || category === 'certificate') && !hasClinicalContext && (
                <Typography variant="body2" sx={{ color: '#94a3b8', py: 3 }}>
                  Preencha a identificação do atendimento na evolução para continuar.
                </Typography>
              )}
            </Box>
          </Box>
          </Box>
          {assistantQueue.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#ecfdf5',
                px: 2,
                py: 1.25,
                maxHeight: 148,
                overflowY: 'auto',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#047857', display: 'block', mb: 0.75 }}>
                Na fila ({assistantQueue.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {assistantQueue.map((printable) => (
                  <Box
                    key={printable.clientKey}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'space-between',
                      bgcolor: '#fff',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      border: '1px solid #a7f3d0',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        color: '#334155',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      <Box component="span" sx={{ color: '#0d9488', mr: 0.75 }}>
                        {categoryLabel(printable.category)}
                      </Box>
                      {printable.templateName}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Remover da fila"
                      onClick={() => removeFromAssistantQueue(printable.clientKey)}
                      sx={{ flexShrink: 0 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa', gap: 1 }}
        >
          <Button onClick={discardWizardAndClose} sx={{ textTransform: 'none', color: '#64748b' }}>
            {assistantQueue.length > 0 ? 'Descartar fila e fechar' : 'Fechar'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            disabled={assistantQueue.length === 0}
            onClick={flushAssistantQueueAndClose}
            startIcon={<CheckCircleIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#14B8A6',
              '&:hover': { bgcolor: '#0d9488' },
              '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' },
            }}
          >
            Adicionar à evolução{assistantQueue.length ? ` (${assistantQueue.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <PrescriptionFormDialog
        open={prescriptionDialogOpen}
        onClose={() => {
          setPrescriptionDialogOpen(false);
          setPrefillRxTemplate(null);
        }}
        patientId={patientId}
        professionalId={professionalId}
        initialPatient={initialPatient}
        initialProfessional={initialProfessional}
        prefillTemplate={prefillRxTemplate}
        onSuccess={handleClinicalSuccess}
      />

      <CertificateFormDialog
        open={certificateDialogOpen}
        onClose={() => {
          setCertificateDialogOpen(false);
          setPrefillCertTemplate(null);
        }}
        patientId={patientId}
        professionalId={professionalId}
        initialPatient={initialPatient}
        initialProfessional={initialProfessional}
        prefillTemplate={prefillCertTemplate}
        onSuccess={handleClinicalSuccess}
      />

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {editorTemplate?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              O texto será colocado na fila do assistente; você pode acumular outros documentos antes de confirmar na evolução.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setEditorOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            label="Preenchimento"
            placeholder="Digite pedido de exame, laudo, risco cirúrgico..."
            value={editorBody}
            onChange={(e) => setEditorBody(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
          <Button onClick={() => setEditorOpen(false)} sx={{ textTransform: 'none' }}>
            Voltar
          </Button>
          <Button
            variant="contained"
            onClick={commitEditor}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}
          >
            Adicionar à fila
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EvolutionPrintablesWizard;
